import type { ReactNode } from 'react';
import { Skeleton } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { formatMoneyText } from '@/components/ui/Money';
import { cx } from '@/lib/cx';
import {
  DASHBOARD_CURRENCY,
  compactFigureParts,
  compactMoneyParts,
  formatActualAmount,
  formatTargetAmount,
  type FigureParts,
  type FunnelMetric,
} from '@/lib/targets';
import type { TargetActual, TargetMetric } from '@/lib/types';

/**
 * THE FUNNEL TILES — one implementation, mounted by every dashboard that shows
 * targets beside actuals.
 *
 * ⚠️ **EXTRACTED FROM `UniversityPartnershipsPage` ON 2026-09-02, NOT COPIED,
 * AND THE DIFFERENCE IS THE WHOLE POINT.** The Management Dashboard shows the
 * same five figures for a GROUP and for the ORGANISATION that the partnerships
 * page shows for one person. Two copies would agree the day they were written
 * and drift on the first correction — and what would drift here is a set of
 * rules about when a figure may be shown as a zero, every one of which was
 * learned from a defect:
 *
 *   `target: null`   nobody committed. NEVER `0`, never `0%`, never an empty bar
 *   `actual: null`   the figure CANNOT BE COMPUTED. Never `0` (owner, 2026-08-31)
 *   `actual: '0.00'` a real, correct answer — nothing closed in this window
 *   an error         we could not find out
 *
 * Four states that look identical the moment any two are collapsed, and only
 * two of them are a problem.
 *
 * 🔴 **THE TILE -> METRIC MAPPING MOVED ONTO `FUNNEL_TILES` ON 2026-09-11, AND
 * `TILE_METRIC` IS NO LONGER READ BY ANYTHING HERE.**
 *
 * *SUPERSEDED: "`TILE_METRIC` STAYS IN `use-my-targets.ts` and `FUNNEL_TILES`
 * LIVES HERE, which splits one mapping across two files … **Changing an id in
 * one without the other silently detaches a tile from its metric** and it
 * renders 'Not reported' — so change both."*
 *
 * That hazard is what closed it. The Invoiced tile had to stop resolving to
 * `booked` (ACCEPTED QUOTES) and start resolving to the new `invoiced` metric,
 * and a mapping living in a second file is a mapping the next editor does not
 * know to change. **One tile now carries its own metric, so the two cannot
 * disagree.**
 *
 * ✅ **`TILE_METRIC` IS DELETED — done the same day, in `use-my-targets.ts`.**
 *
 * *SUPERSEDED: "⚠️ **`TILE_METRIC` IS NOW AN EXPORT WITH NO IMPORTERS**, and
 * this file cannot delete it — `use-my-targets.ts` is outside this change. It
 * still says `invoiced: 'booked'`, which is the old, wrong answer.
 * **Unreferenced code is consulted as documentation and it is the copy nothing
 * has ever tested**, so whoever owns that module should delete it rather than
 * re-point it."*
 *
 * The advice was taken: the map is gone and **what stands in its place is a
 * dated removal-record comment** naming why `invoiced: 'booked'` was the wrong
 * answer. There is no export, so there is nothing to re-point and nothing to
 * consult as documentation.
 *
 * ⛔ **SO A GREP FOR `TILE_METRIC` FINDS ITS OWN TOMBSTONE, AND THIS BLOCK READ
 * THAT AS THE EXPORT SURVIVING.** That is this codebase's own *grep the SURFACE,
 * never the NAME* trap — a comment naming a key makes the key greppable, so the
 * grep proving it was removed finds the note saying so — arriving on the
 * removal record itself. **Do not "fix" it by deleting either comment**:
 * supersede-don't-delete is what makes the removal legible, and a reader
 * meeting `FUNNEL_TILES` carrying its own metric with no history cannot tell it
 * from a mapping that was always there. **Check for a DECLARATION, not a
 * mention.**
 */

/**
 * The house label style for a tile's name. Shared so five tiles cannot drift.
 *
 * Sentence case, muted, `text-xs` — the same label `ui/StatTile` uses. ⚠️ *SUPERSEDED
 * 2026-10-11: `text-2xs font-semibold uppercase tracking-[0.06em] text-fg-subtle`* —
 * the grey caps eyebrow was the tile template's loudest tell. The labels in
 * `FUNNEL_TILES` are already written in sentence case, so dropping `uppercase`
 * needs no copy change.
 */
export const TILE_FIELD_LABEL = 'text-xs font-medium text-fg-muted';

/** The figure style every tile on the target dashboards shares — `ui/StatTile`'s value. */
export const TILE_FIGURE = 'tabular m-0 text-2xl font-semibold text-fg';

/**
 * The five tiles, in funnel order: what is in play, what has been quoted, what
 * was accepted, what has been paid, how many deals closed.
 *
 * ⚠️ Only an id and a label. The figures used to come from a `placeholder-data`
 * module, which also supplied an invented value, target and attainment for each
 * — every one of those is now read from the server, so the import went rather
 * than being left half-used. **That module no longer exists**: it was deleted on
 * 2026-08-31 along with the Overview dashboard, which was its only other
 * importer. The name is left here unlinked and in past tense deliberately — it
 * explains why these tiles carry no numbers of their own, which is still the
 * thing a reader needs — but do not go looking for the file, and do not
 * "restore" a figure from it.
 *
 * ⚠️ **EACH TILE NAMES ITS OWN METRIC, so a label and the figure under it cannot
 * come apart in a second file.** The `id` is now only a React key and a test
 * handle; the `metric` is the contract.
 *
 * 🔴 **`invoiced` RESOLVES TO THE `invoiced` METRIC AND NO LONGER TO `booked`
 * — owner's decision, 2026-09-11.** `booked` is ACCEPTED QUOTES and reads
 * "Accepted revenue" now; this tile is invoices BILLED in the window. Measured
 * on production that day, the old mapping put **INVOICED £0 beside COLLECTED
 * £96.4K** on the Management Dashboard, because the cycle's one accepted quote
 * is in EUR.
 *
 * ⚠️ **UNTIL THE SERVER SHIPS `invoiced` THIS TILE HAS NO ROW AND RENDERS A
 * DASH.** `TileFigures` answers an absent row with "no figure", never `0.00` —
 * a zero here would be a positive claim that no invoice was raised. Verified
 * against the live API on 2026-09-11, which answered five metrics and not this
 * one. That is the deploy-order window, not a fault.
 *
 * ⚠️ **`won-deals` is a COUNT.** It carries no currency symbol and no gold unit
 * suffix, and that is enforced in `compactFigureParts` by the metric rather than
 * by this list — a tile cannot opt itself into money by being labelled like it.
 */
export const FUNNEL_TILES: readonly { id: string; label: string; metric: FunnelMetric }[] = [
  { id: 'pipeline', label: 'Pipeline', metric: 'pipeline' },
  { id: 'quoted', label: 'Quoted', metric: 'quoted' },
  { id: 'invoiced', label: 'Invoiced', metric: 'invoiced' },
  { id: 'collected', label: 'Collected', metric: 'collected' },
  { id: 'won-deals', label: 'Won deals', metric: 'won_deals' },
];

/**
 * A compacted figure, with its unit suffix painted apart from its digits.
 *
 * ⚠️ **THE THREE PIECES MUST RENDER BACK TO BACK WITH NO WHITESPACE BETWEEN
 * THEM.** The DOM text has to stay exactly `£20K`, because that is what a screen
 * reader announces and what a selection copies; JSX drops the newline-and-indent
 * between adjacent expression containers, which is the only reason this can be
 * written across three lines at all. Anything that introduces a real break —
 * a space, a `display: block`, an `aria-hidden` on one piece, a wrapper that is
 * not `inline` — splits one figure into two things read separately.
 *
 * ⚠️ **Only the UNIT is set apart** — muted, never a colour. *SUPERSEDED
 * 2026-10-11: the unit was painted `text-accent-gold`*, which read as a
 * highlighted digit rather than as a unit and disagreed with every grey currency
 * code elsewhere in the app. Not the currency symbol, not the digits, not the
 * separators. `compactFigureParts` is what decides where the boundary falls; do
 * not re-derive it here by slicing the last character.
 */
export function CompactFigure({ parts }: { parts: FigureParts }) {
  return (
    <>
      {parts.head}
      {parts.unit === '' ? null : <span className="text-fg-muted">{parts.unit}</span>}
      {parts.tail}
    </>
  );
}

/**
 * A compacted figure that still carries its EXACT value — on hover (`title`)
 * and to a screen reader (an `sr-only` copy; the compact text is
 * `aria-hidden`). Owner 2026-10-27: every target and commission money figure is
 * K/M, with the exact amount one hover away.
 *
 * ⚠️ `exact` is the caller's long-form string (`formatMoneyText`,
 * `formatActualAmount`, `formatTargetAmount`) — never re-derived here.
 */
export function ExactFigure({ parts, exact }: { parts: FigureParts; exact: string }) {
  return (
    <span title={exact}>
      <span aria-hidden="true">
        <CompactFigure parts={parts} />
      </span>
      <span className="sr-only">{exact}</span>
    </span>
  );
}

/**
 * Compact money (`US$885.74K`, `£2.30M`) for the target and commission
 * surfaces, from `compactMoneyParts` — the one compact formatter. The exact
 * decimal and its ISO code ride on `ExactFigure`. Money stays a decimal string;
 * `null`/blank renders `—`, never `0`.
 */
export function CompactMoney({
  amount,
  currency,
  className,
}: {
  amount: string | null | undefined;
  currency: string;
  className?: string;
}) {
  const text = amount?.trim() ?? '';
  if (text === '') return <span className={cx('tabular-nums', className)}>—</span>;
  return (
    <span className={cx('tabular-nums whitespace-nowrap', className)}>
      <ExactFigure parts={compactMoneyParts(text, currency)} exact={formatMoneyText(text, currency)} />
    </span>
  );
}

/**
 * The dash a tile shows when there is no figure to show.
 *
 * ⚠️ **A dash is "no figure", never "zero".** `actual: '0.00'` is a real answer
 * and renders as a real `0`; this is for the cases where nothing was measured
 * at all. `note` is for a fact about THIS metric only — anything true of the
 * whole panel is said once by the panel's own strip instead of five times here.
 */
export function NoFigure({ note }: { note?: string }) {
  return (
    <>
      <p className="tabular m-0 text-2xl font-semibold text-fg-subtle">—</p>
      {note !== undefined && <p className="m-0 text-xs text-fg-subtle">{note}</p>}
    </>
  );
}

/*
 * The mark a NET NEGATIVE attainment leaves in the track — a stub, sized to the
 * track's own height so it reads as a square notch rather than as progress.
 * Deliberately a fixed length and not a percentage: it is not measuring
 * anything, it is saying "this went the other way", and the figure below it
 * carries how far.
 */
const NET_NEGATIVE_BAR_WIDTH = '0.375rem';

/**
 * The attainment meter. **Geometry only — it is never the number beside it.**
 *
 * 🔴 **`Math.min(pct, 100)` CLAMPED THE TOP AND NOTHING ELSE, AND THE BOTTOM IS
 * THE END THAT FAILS SILENTLY.** At `pct = -25` that emitted `width: -25%`,
 * which is invalid CSS: the browser drops the declaration, the inner element is
 * block-level, its width falls back to `auto` — and `auto` inside this track is
 * the FULL track. So a loss rendered as a completely full bar, immediately
 * above the words "-25% of target". Maximum success and a loss, side by side,
 * on the post-login landing page, with nothing thrown, nothing logged and a
 * green `tsc`. **Clamp both ends; an unclamped low end is not a smaller bar,
 * it is the largest one.**
 *
 * ⚠️ **A NET NEGATIVE AND "NOTHING YET" MUST NOT RENDER ALIKE**, which is why
 * the low clamp is not simply `Math.max(pct, 0)`. Those are two different
 * facts — *nobody has raised anything on these accounts* versus *this account
 * is net negative* — and an empty track is already spoken for by the first.
 * The negative case therefore paints a short `danger`-toned stub: an empty
 * track and a marked one differ by the PRESENCE of a mark, not only by its
 * colour, so the distinction survives for a reader who cannot separate the two
 * hues. The bar is `aria-hidden` regardless — the percentage beside it is the
 * accessible signal, and it is the one that carries the sign.
 *
 * ⚠️ **The PERCENTAGE is not clamped at either end and must never be.** 120%
 * is the interesting number when a target is beaten, and `-25%` is the truth
 * when it is not. Only the geometry is clamped; clamping the figure would hide
 * the very thing this guard exists to make legible.
 *
 * ⚠️ **NO `actual` CAN BE NEGATIVE TODAY, so this branch is unreachable — that
 * is why it is written down rather than left to be discovered.** Line subtotals
 * are floored at zero in the database, so every metric sums to zero or more.
 * Removing that floor is a separate, approved project; the day it lands this
 * page starts receiving negative actuals with no frontend change, nothing in a
 * diff to review and no instrument in this repo able to see it. **Do not delete
 * this as dead code because you could not produce the state.**
 */
export function AttainmentBar({ pct }: { pct: number }) {
  const negative = pct < 0;

  return (
    <div aria-hidden="true" className="h-1.5 overflow-hidden rounded-xs bg-surface-sunken">
      <div
        className={cx('h-full rounded-xs', negative ? 'bg-danger' : 'bg-fg-secondary')}
        style={{ width: negative ? NET_NEGATIVE_BAR_WIDTH : `${Math.min(pct, 100)}%` }}
      />
    </div>
  );
}

/**
 * What the WHOLE panel is doing, resolved once.
 *
 * ⚠️ **THE POINT OF THIS TYPE IS THAT FOUR OF THE FIVE CASES ARE FACTS ABOUT
 * THE PANEL, NOT ABOUT A METRIC.** They used to be branched inside the tile, so
 * every one of them printed the identical sentence five times down a row — five
 * "No period covers today", five "Figures could not be read." That is what made
 * a dashboard with nothing on it read as broken rather than as empty, which is
 * the complaint this shape exists to answer. A per-metric fact stays in the
 * tile; anything else is worded once, by the caller's own strip.
 *
 * ⚠️ **`noTargetsSet`, `allZero` and `anyExcluded` are on the STATE, not
 * recomputed by each consumer.** The strip and the tiles both read them, and the
 * tiles suppress their own "No target set" line exactly when the strip carries
 * it — two consumers, one predicate, so a panel cannot end up saying it twice or
 * not at all.
 *
 * ⚠️ **`refused` IS ITS OWN MEMBER AND IS NOT AN `error`.** A 403 says *you may
 * not ask*, which is a different sentence from *we could not find out* and sends
 * a reader somewhere else entirely. It is carried here rather than left to the
 * caller so the tiles can fall to a dash for it without a second predicate.
 */
export type TileState =
  | { kind: 'blocked' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'refused'; message: string }
  | { kind: 'no-period' }
  | {
      kind: 'figures';
      byMetric: Partial<Record<TargetMetric, TargetActual>>;
      noTargetsSet: boolean;
      allZero: boolean;
      /**
       * Whether ANY metric dropped a record for its currency.
       *
       * ✅ **EXCLUSION NOTES RENDER AGAIN — the GBP-only ruling was withdrawn on
       * 2026-09-11 (owner) and both dashboards now carry a currency switcher.**
       *
       * *SUPERSEDED: "🔴 **NOTHING RENDERS THIS ANY MORE — the dashboards are
       * GBP ONLY as of 2026-09-02 (owner), so there is no second currency for a
       * figure to be qualified against and no exclusion note anywhere on these
       * panels.** … **do not reintroduce a sentence off it without the owner**:
       * a note naming a currency was exactly what was removed."*
       *
       * **Both halves are dead.** The ruling is reversed — see `TileFigures`,
       * where it was withdrawn because forcing GBP hid roughly 5% of the
       * business behind `excludedByCurrency: 234` with no unit printed — and the
       * owner has since ASKED for the note, so the permission that bullet
       * withheld has been given.
       *
       * ⚠️ **BUT THIS BOOLEAN IS STILL READ BY NOBODY, AND THAT IS NOW A
       * DIFFERENT FACT RATHER THAN THE SAME ONE.** The panels word their notes
       * off the PER-METRIC counts (`row.excludedByCurrency`, through
       * `excludedNote` and `ExcludedByCurrencyStrip`), because a bare "something
       * was left out" under five tiles cannot say WHICH figure it qualifies —
       * which is the entire point of the note. A flag that answers "any" cannot
       * name a metric.
       *
       * So it is kept as the honest reading of the response and as the cheap
       * test a future per-tile line would gate on. ⛔ **What must not come back
       * is a sentence rendered off THIS flag** — not because the note is
       * unwanted, but because an unattributed one is the version that was found
       * unreadable.
       */
      anyExcluded: boolean;
    };

/**
 * What the panel is doing, from the four things any caller knows.
 *
 * ⚠️ **THE ORDER OF THESE TESTS IS THE CONTRACT, not a style.** `blocked` first
 * because it is not an error and not an empty answer — it is "you have not
 * finished asking", and the reason is worded on the control that can fix it, so
 * nothing is repeated here. `refused` before `error` because a 403 that fell
 * through to the error branch would tell somebody the server broke when it
 * merely declined.
 */
export function tileState(input: {
  blocked?: boolean;
  loading: boolean;
  /** A 403. Its own state — see `TileState`. */
  refused?: string | null;
  error: string | null;
  noPeriod?: boolean;
  byMetric: Partial<Record<TargetMetric, TargetActual>>;
}): TileState {
  if (input.blocked === true) return { kind: 'blocked' };
  if (input.loading) return { kind: 'loading' };
  if (input.refused != null) return { kind: 'refused', message: input.refused };
  if (input.error !== null) return { kind: 'error', message: input.error };
  if (input.noPeriod === true) return { kind: 'no-period' };

  const rows = FUNNEL_TILES.map((tile) => input.byMetric[tile.metric]).filter(
    (row): row is TargetActual => row !== undefined,
  );

  return {
    kind: 'figures',
    byMetric: input.byMetric,
    /*
     * ⚠️ **`rows.length > 0` is not defensive.** With no rows AT ALL every tile
     * renders "Not reported", and that must not be softened into the calm
     * "nobody has set a target yet" note, which would present a broken response
     * as a normal empty dashboard.
     *
     * ⚠️ **A SINGLE absent metric is a different thing and is expected today.**
     * *SUPERSEDED: "the server promises all five metrics, so that is a contract
     * break".* Since 2026-09-11 the funnel names `invoiced`, which the deployed
     * server did not answer when this was written — so four rows here is the
     * ordinary deploy-order state and only ZERO rows is a contract break.
     */
    noTargetsSet: rows.length > 0 && rows.every((row) => row.target === null),
    /*
     * ⚠️ Read off what the server SENT, and worded that way by the caller.
     * "Every figure is zero" is the claim the response supports; "nothing has
     * happened" is not.
     *
     * ⚠️ **An `actual: null` row is NOT zero and must not make this true.**
     * `Number(null)` is `0` in JavaScript, so the null check is load-bearing
     * rather than belt-and-braces: without it an unattributable group — every
     * metric unmeasurable — would report `allZero`, and the caller's strip would
     * calmly announce that a team achieved nothing when the truth is that
     * nothing about it can be computed at all.
     */
    allZero:
      rows.length > 0 && rows.every((row) => row.actual !== null && Number(row.actual) === 0),
    /*
     * ⚠️ `> 0` on each row rather than a sum compared to zero, so a `NaN` from
     * a server that omitted the key cannot poison the whole flag — `NaN > 0` is
     * false, and the tiles fall silent on exactly the same test.
     */
    anyExcluded: rows.some((row) => (row.excludedByCurrency ?? 0) > 0),
  };
}

/**
 * A tile's numbers: the actual as the headline, the target under it, and the
 * meter and percentage between them.
 *
 * ⚠️ **THREE STATES THAT LOOK IDENTICAL IF COLLAPSED, AND ONLY ONE IS A
 * PROBLEM.** An error means we could not find out. A null target means nobody
 * committed to a number — NOT zero, because committing to nothing and
 * committing to zero are different, and showing `0` for both invites the
 * reading that a target was missed that never existed. An actual of `0.00` is
 * a real, correct answer: nothing has closed in this window.
 *
 * ⚠️ **NO TARGET MEANS NO BAR — not a zero-width one, and no percentage.** A
 * bar sitting empty under "0% of target" reads as total failure against a
 * commitment nobody ever made, which is the misreading the whole `target: null`
 * contract exists to prevent. `x% of nothing` is not a smaller number either;
 * it is undefined, so the percentage is drawn only where a target exists.
 *
 * ⚠️ **The actual is formatted as an ACTUAL and the target as a TARGET**, and
 * the argument is not decoration: a money row with no currency is a broken row
 * on the second and the ordinary empty state on the first. See `FigureRole`.
 *
 * 🔴 **THE CURRENCY IS A PROP NOW — 2026-09-11. `DASHBOARD_CURRENCY` IS NO
 * LONGER READ HERE.**
 *
 * *SUPERSEDED: "EVERY MONEY FIGURE HERE IS `DASHBOARD_CURRENCY` … owner's
 * instruction, 2026-09-02: the dashboards are GBP only. So there is no per-row
 * currency to plumb through, no second currency for a figure to be qualified
 * against, and no exclusion note."*
 *
 * That ruling is withdrawn. It was hiding most of the business: `/targets/actuals`
 * forced GBP, and measured on production 2026-09-11 the collected figure came
 * back with **`excludedByCurrency: 234`** against 652 USD and 89 EUR invoices —
 * roughly 5% of the business on screen, **with no unit printed on the number**.
 *
 * ⚠️ **WHY THIS HAD TO BE A PROP AND NOT A CONSTANT, said plainly: the caller
 * can now ask for USD.** While this file formatted with a hardcoded GBP, a USD
 * request would have rendered USD amounts labelled `GBP` — a wrong number
 * wearing a plausible unit, which is strictly worse than the understatement it
 * replaced, because nothing about it looks wrong.
 *
 * ⚠️ **PASS THE CURRENCY THE SERVER SAYS IT MEASURED IN**
 * (`ActualsState.measuredInCurrency`), never the one the picker is set to and
 * never a metric's `row.baseCurrency` — that one is the stored TARGET's
 * currency, `null` on a count and on any metric nobody set a target for, and it
 * legitimately disagrees.
 *
 * `won_deals` is unaffected either way: it is the one COUNT metric, and
 * `compactFigureParts` drops the currency by the METRIC rather than by what is
 * passed here, so handing it a code cannot make it read as money.
 */
export function TileFigures({
  metric,
  state,
  currency = DASHBOARD_CURRENCY,
  onOpen,
  label,
}: {
  /**
   * 🔴 **THE METRIC ITSELF SINCE 2026-09-11, where this took a `tileId` and
   * looked it up in `TILE_METRIC` — a map in another module.** A tile whose
   * label and whose figure are decided in two files is a tile that can be
   * relabelled without being re-pointed, which is exactly what "Invoiced"
   * showing ACCEPTED QUOTES was. `FUNNEL_TILES` carries the pair now.
   */
  metric: TargetMetric;
  state: TileState;
  /** Defaults to GBP, so an un-migrated caller is byte-for-byte unchanged. */
  currency?: string;
  /**
   * Owner 2026-10-28 (target tile drill-down): when given, the FIGURE becomes a
   * `<button>` that opens the rows behind it. Only a real figure is clickable —
   * a dash, a skeleton, "Not reported" and "Cannot be measured" stay inert,
   * because there are no rows behind a figure that does not exist. Absent →
   * byte-for-byte the old `<p>`.
   *
   * ⚠️ The button holds only the figure (`ExactFigure`: plain spans and a
   * `title`). Nothing interactive may be nested inside it — the target line,
   * the bar and the `below` slot stay outside.
   */
  onOpen?: () => void;
  /** The tile's label, for the button's accessible name. Required with `onOpen`. */
  label?: string;
}) {
  if (state.kind === 'loading') return <Skeleton className="h-9 w-28" />;

  /*
   * ⚠️ Blocked, refused, failed and no-period are the panel's business, and the
   * caller's strip has already said which one it is. The tile shows only that it
   * holds no figure — a dash, never a zero.
   */
  if (state.kind !== 'figures') return <NoFigure />;

  /*
   * 🔴 **AN ABSENT ROW IS "NO FIGURE" AND MAY NEVER BE `0.00`, AND SINCE
   * 2026-09-11 THIS IS A LIVE BRANCH RATHER THAN A BORDER GUARD.**
   *
   * The funnel names `invoiced`, the server ships it after this client does
   * (server first, client second is the standing rule and it is the safe
   * order), so for the length of that window this metric simply is not in the
   * response. Verified against the live API on 2026-09-11: it answered
   * `pipeline`, `quoted`, `booked`, `collected` and `won_deals`, and not
   * `invoiced`.
   *
   * ⚠️ **A zero here would be a CLAIM that no invoice was raised**, which is a
   * statement about the business made out of a gap in a deploy. The dash says
   * only that we were not told.
   */
  const row = state.byMetric[metric];
  if (row === undefined) return <NoFigure note="Not reported" />;

  /*
   * ⚠️ **A FOURTH state, and the ONE thing it must never render as is a zero.**
   * `actual: null` means the figure CANNOT BE COMPUTED — it arrives only for a
   * target set against a user group that names no relationship any revenue
   * document records (`delivery_lead`, `approvers`), where the server returns
   * before any metric query runs. A `?? 0` here would draw a full-width empty
   * bar under "0% of target", which reads as total failure against a real
   * commitment.
   *
   * ⚠️ **THE MANAGEMENT DASHBOARD REACHES THIS, AND THE PARTNERSHIPS PAGE DOES
   * NOT.** The partnerships page asks for `subjectType: 'user'` and only that,
   * so it can never see a null actual; the Management Dashboard asks about user
   * GROUPS, which is the one subject that produces one. Since 2026-09-02 this is
   * therefore a LIVE branch rather than a border guard — but it is still only
   * half the answer, and the caller must render `attribution.reason` verbatim
   * beside the tiles. A dash saying "Cannot be measured" says the figure is
   * missing; only the server's sentence says why, and why is the actionable
   * half.
   */
  const actualAmount = row.actual;
  if (actualAmount === null) return <NoFigure note="Cannot be measured" />;

  const actual = Number(actualAmount);
  const target = row.target === null ? null : Number(row.target);
  /*
   * ⚠️ Guarded on the target being a positive number, not merely non-null. A
   * target of zero divides to Infinity, which renders as "Infinity% of target"
   * — and zero is a legal thing to commit to.
   */
  const pct =
    target !== null && Number.isFinite(target) && target > 0 && Number.isFinite(actual)
      ? Math.round((actual / target) * 100)
      : null;
  const exactActual = formatActualAmount(row.metric, actualAmount, currency);
  const figure = (
    <ExactFigure
      parts={compactFigureParts(row.metric, actualAmount, currency, 'actual')}
      exact={exactActual}
    />
  );
  return (
    <>
      {/*
        * `title` carries the EXACT decimal, pence and currency code included.
        * The headline drops both by design — see `compactFigureParts` — so this
        * is the only place the precise figure survives, and it is why dropping
        * the pence is a display rule rather than a loss.
        *
        * ⚠️ With `onOpen` the accessible name is "Open <label> details: <exact>"
        * — the aria-label replaces the button's content for a screen reader, so
        * the exact figure is carried in the label or it would be lost.
        */}
      {onOpen === undefined ? (
        <p className={TILE_FIGURE}>{figure}</p>
      ) : (
        <p className="m-0">
          <button
            type="button"
            onClick={onOpen}
            aria-label={`Open ${label ?? 'figure'} details: ${exactActual}`}
            className={cx(
              TILE_FIGURE,
              'cursor-pointer rounded-sm border-0 bg-transparent p-0 text-left underline-offset-4 decoration-hairline-strong hover:underline focus-visible:underline',
            )}
          >
            {figure}
          </button>
        </p>
      )}

      {row.target === null ? (
        /*
         * ⚠️ Never `£0` and never `0%`. `target: null` is "nobody set one",
         * which is a different fact from a target of zero and from a target
         * that was missed.
         *
         * ⚠️ **Suppressed only when the caller's strip carries it for every
         * tile**, never merely because it is repetitive: with some targets set
         * and others not, this line is the only thing separating the two, and it
         * stays. Both branches read `state.noTargetsSet`, so the sentence
         * appears exactly once either way.
         */
        state.noTargetsSet ? null : <p className="m-0 text-xs text-fg-subtle">No target set</p>
      ) : (
        <p className="tabular m-0 text-xs text-fg-muted">
          of{' '}
          <ExactFigure
            parts={compactFigureParts(row.metric, row.target, currency, 'target')}
            exact={formatTargetAmount(row.metric, row.target, currency)}
          />{' '}
          target
        </p>
      )}

      {pct !== null && (
        <>
          {/* The bar clamps at BOTH ends and a net negative is drawn as its own
            * mark rather than as an empty track — see `AttainmentBar`. The
            * percentage below is left unclamped in both directions, because
            * 120% and -25% are each the real answer. */}
          <AttainmentBar pct={pct} />
          <p className="tabular m-0 text-xs text-fg-muted">{pct}% of target</p>
        </>
      )}
    </>
  );
}

/**
 * The five tiles as one hairline grid.
 *
 * Hairlines are 1px grid gaps showing the border colour through — an empty cell
 * paints as a solid slab of it. Five tiles orphan one at the two-column step, so
 * the last tile spans both columns at `sm` to close the row; `lg` fits all five
 * and releases the span.
 */
export function TargetTileGrid({
  state,
  labelledBy,
  currency = DASHBOARD_CURRENCY,
  below,
  onOpenMetric,
}: {
  state: TileState;
  labelledBy: string;
  /**
   * The currency the figures were MEASURED in — see `TileFigures`. Defaults to
   * GBP so every existing caller is unchanged.
   */
  currency?: string;
  /**
   * Extra content rendered inside a metric's tile, under its figures (after the
   * attainment bar). Owner 2026-10-25: the Invoiced tile hosts the commission
   * progress strip on the University Partnerships dashboard. The slot is
   * independent of the tile's own state — it renders whether the tile shows a
   * bar, "No target set" or a dash. Absent → nothing rendered (Management is
   * unchanged).
   */
  below?: Partial<Record<FunnelMetric, ReactNode>>;
  /**
   * Owner 2026-10-28: when given, each tile's figure is a button that calls
   * this with the tile's metric (the drill-down modal is the caller's, mounted
   * once per page). Absent → every tile renders exactly as before.
   */
  onOpenMetric?: (metric: FunnelMetric) => void;
}) {
  /*
   * `FUNNEL_TILES` is typed by the wider `TargetMetric`; widening the record's
   * key type is safe because every key is optional.
   */
  const slots: Partial<Record<TargetMetric, ReactNode>> | undefined = below;
  const lastIndex = FUNNEL_TILES.length - 1;
  /*
   * Only an ODD count orphans a tile at the two-column step — guard on the
   * count, not a hardcoded "fifth tile spans", so an even list isn't spanned.
   */
  const lastTileSpans = FUNNEL_TILES.length % 2 === 1;

  return (
    <ul
      aria-labelledby={labelledBy}
      className="m-0 grid list-none grid-cols-1 gap-px bg-border p-0 sm:grid-cols-2 lg:grid-cols-5"
    >
      {FUNNEL_TILES.map((tile, index) => (
        <li
          key={tile.id}
          className={cx(
            'flex min-w-0 flex-col gap-1.5 bg-surface px-4 py-4',
            index === lastIndex && lastTileSpans && 'sm:col-span-2 lg:col-span-1',
          )}
        >
          <p className={cx('m-0', TILE_FIELD_LABEL)}>{tile.label}</p>
          <TileFigures
            metric={tile.metric}
            state={state}
            currency={currency}
            label={tile.label}
            onOpen={onOpenMetric === undefined ? undefined : () => onOpenMetric(tile.metric)}
          />
          {slots?.[tile.metric] ?? null}
        </li>
      ))}
    </ul>
  );
}

/*
 * ⚠️ **`TileHintStrip` IS GONE — owner's ruling, 2026-09-05.** It was the muted
 * sibling of `TileFailureStrip` below and carried the "no target period covers
 * today" / "no targets are set for this window" / "more people than are listed"
 * sentences on both dashboards. Every one of them explained a state rather than
 * reporting a server error or a refusal, so they went with the rest of this
 * app's helper text. **`TileFailureStrip` stays** — it renders a failure or a
 * refusal in the server's own words, which is a keeper. Do not reintroduce a
 * muted strip to carry an explanation back in under a different name.
 */

/**
 * Something went wrong, or you may not ask — in the server's own words.
 *
 * ⚠️ **`tone` exists because a 403 IS NOT A FAILURE and painting it as one
 * sends somebody to fix a system that is working exactly as configured.** A
 * refusal is `warning`; a genuine load failure is `danger`. Both carry
 * `role="alert"` because both are the reason the figures beside them are
 * missing, and neither may be softened into silence.
 */
export function TileFailureStrip({
  headline,
  message,
  tone = 'danger',
}: {
  headline: string;
  message: string;
  tone?: 'danger' | 'warning';
}) {
  return (
    <Notice tone={tone} role="alert" title={headline}>
      <span className="[overflow-wrap:anywhere]">{message}</span>
    </Notice>
  );
}
